import { Link } from 'react-router';

import { cn } from '@april9au/react-common/utils';

import { DevelopedBy } from '@/components/developed-by';
import { useStrings } from '@/i18n/use-strings';

/**
 * The site footer: SLSA's copyright, the agency credit and the three links a
 * visitor legally expects to find. Content pages render it; the learning flow
 * does not, so a module never offers a way out of itself.
 *
 * The year is rendered from the server's clock, which is deliberately not
 * hydration-sensitive — both sides format the same UTC year.
 */
export function Footer({ className }: { className?: string }) {
  const { content } = useStrings();

  const links = [
    { to: '/privacy', label: content.footer_privacy },
    { to: '/terms', label: content.footer_terms },
    { to: '/contact', label: content.footer_contact },
  ];

  return (
    <footer className={cn('border-surf-gray mt-auto border-t bg-white', className)}>
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 py-6 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        {/* Copyright and credit are one block: both are attribution, and
            pairing them keeps the credit off the links row, where it would read
            as a fourth destination rather than a signature. */}
        <div className="text-muted-foreground flex flex-col gap-1 text-xs">
          <p>
            {content.footer_copyright} {new Date().getUTCFullYear()}
          </p>

          <DevelopedBy className="self-start" />
        </div>

        <nav aria-label={content.footer_nav_label}>
          <ul className="-mx-2 flex flex-wrap items-center gap-x-1 gap-y-1">
            {links.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="text-surf-blue focus-visible:ring-surf-blue/40 inline-flex min-h-11 items-center rounded-full px-2 text-sm font-semibold transition-colors duration-150 hover:underline focus-visible:ring-4 focus-visible:outline-none active:opacity-65 motion-reduce:transition-none"
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </footer>
  );
}
